import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useFormik } from "formik";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import {
  UIInput,
  UIInputDisable,
  UICheckbox,
} from "../../components/Inputfields";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import {
  DesktopTableParts,
  MobileCardsOnly,
} from "components/TableandMobilecomponent";
import { showError, showSuccess } from "toast/toast";
import { formatTrackingNumberEmailHtml } from "../../utils/publicTrackingUrl";
import AddressAutocomplete from "components/AddressAutocomplete";
import { US_STATES, getUsStateByCode, getUsStateByName } from "constants/usStates";

// API calls
import {
  searchShippingOrders,
  ShippingOrder,
  getWalletBalance,
  getCarrierAccounts,
  getCarrierPackageDetails,
  CarrierAccount,
  ShippingPresetSelect2Result,
  getPackageTemplatesSelect2,
  type PackageTemplateSelect2Result,
  getShippingRates,
  buyShippingLabel,
  markOrderAsShipped,
  getOrderById,
  downloadEasyPostLabelPdf,
  Rate,
  GetRatesResponse,
  BuyLabelResponse,
  getCustomerGroupUsers,
  type CustomerGroupUser,
  getAddressesAllSelect2,
  type AddressSelect2Item,
  getAddressById,
} from "./Shipmentsapicalls";
import { sendTriggerEmails } from "utils/emailService";

/* ---------------- SHIPPING RATES TABLE COLUMN HELPER (outside component) ---------------- */
import { getAuthCookie } from "../../utils/authCookies";

type ShippingRateRow = {
  carrierType: string;
  service: string;
  rate: string;
  currency: string;
  insurance_value?: string;
  insurance_fee_estimate?: string;
  rate_total?: string;
  rateId?: string;
};

const columnHelper = createColumnHelper<ShippingRateRow>();

const shippingRateColumns = [
  columnHelper.accessor("carrierType", {
    header: "Carrier Type",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor("service", {
    header: "Service & Est Delivery Days",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor("rate", {
    header: "Rate",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor("currency", {
    header: "Currency",
    cell: (info) => info.getValue(),
  }),
];

// Empty data array - defined outside component to prevent re-renders
const EMPTY_SHIPPING_RATES: ShippingRateRow[] = [];

/* ---------------- REQUEST UUID GENERATION ---------------- */
const UNIQUE_ID_KEY = "re_buylabel_unique_id";

async function ensureDraftUniqueId(token?: string): Promise<string | null> {
  try {
    // 1) if already cached, return it
    const existing = localStorage.getItem(UNIQUE_ID_KEY);
    if (existing) return existing;

    if (!token) return null;

    // 2) else fetch one from API and cache it
    const res = await fetch(`${process.env.REACT_APP_API_URL}/utils/new-unique-id`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!res.ok) return null;

    const data = await res.json().catch(() => ({}));
    const id = data?.id || data?.data?.id || data?.data?.unique_id || data?.unique_id || null;
    if (!id) return null;
    localStorage.setItem(UNIQUE_ID_KEY, id);
    return id;
  } catch {
    return null;
  }
}

/* ---------------- TYPES ---------------- */

type BuyLabelProps = {
  baseRoute?: string;
  shipping_integration_id?: string;
};

type OrderOption = {
  value: string;
  label: string;
  raw: ShippingOrder;
};

type AddressOption = {
  value: string;
  label: string;
};

type PresetOption = {
  value: string;
  label: string;
  raw: ShippingPresetSelect2Result;
};

type CustomerOption = {
  value: string;
  label: string;
  name: string;
  email: string;
  raw: CustomerGroupUser;
};

type AddressSelect2Option = {
  value: string;
  label: string;
  raw: AddressSelect2Item;
};



// // Helper function to get state name from state code
// const getStateName = (stateCode: string): string => {
//   const state = STATE_OPTIONS.find((s) => s.value === stateCode);
//   if (state) {
//     // Extract state name from label (e.g., "Alabama (AL)" -> "Alabama")
//     return state.label.split(" (")[0];
//   }
//   return stateCode; // Fallback to code if not found
// };

// Helper function to get country name from country code
const getCountryName = (countryCode: string): string => {
  const countryMap: Record<string, string> = {
    US: "United States",
    CA: "Canada",
    MX: "Mexico",
    GB: "United Kingdom",
    // Add more as needed
  };
  return countryMap[countryCode] || countryCode;
};

// Helper function to normalize country code (ensures we always get code, not name)
const normalizeCountryCode = (countryValue: string): string => {
  // If it's already a code (2-3 letters), return it
  if (countryValue && countryValue.length <= 3) {
    return countryValue.toUpperCase();
  }
  
  // If it's a full name, convert to code
  const reverseCountryMap: Record<string, string> = {
    "United States": "US",
    "Canada": "CA",
    "Mexico": "MX",
    "United Kingdom": "GB",
    // Add more as needed
  };
  
  return reverseCountryMap[countryValue] || "US"; // Default to US
};

const US_COUNTRY_CODE = "US";

type PackageDimFields = {
  length?: number | string;
  width?: number | string;
  height?: number | string;
  weight?: number | string;
};

/** Read L/W/H (and optional weight) from preset/template API shapes. */
const extractPackageDimensions = (raw: unknown): PackageDimFields | null => {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const dim = (r.package ?? r.dimensions ?? r.package_dimensions) as PackageDimFields | undefined;
  if (!dim || typeof dim !== "object") return null;
  const { length, width, height, weight } = dim;
  if (length == null && width == null && height == null) return null;
  return { length, width, height, weight };
};

const dimFieldString = (v: number | string | null | undefined): string =>
  v != null && v !== "" ? String(v) : "";

// Helper function to normalize state code (ensures we always get code, not name)
// const normalizeStateCode = (stateValue: string): string => {
//   // If it's already a code (2 letters), return it
//   if (stateValue && stateValue.length === 2) {
//     return stateValue.toUpperCase();
//   }
  
//   // If it's a full name, find the code from STATE_OPTIONS
//   const state = STATE_OPTIONS.find((s) => {
//     const stateName = s.label.split(" (")[0];
//     return stateName.toLowerCase() === stateValue.toLowerCase();
//   });
  
//   return state ? state.value : stateValue; // Return code if found, else return original
// };

// Carrier and Package options are now fetched dynamically from API

type Warehouse = {
  _id: string;
  label: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    zip?: string;
    phone?: string;
  };
};

/* ================================================= */

const BuyLabel = ({ baseRoute = "/signage", shipping_integration_id }: BuyLabelProps) => {
  const navigate = useNavigate();
  // Get token and userId once using useRef to avoid re-renders
  const tokenRef = useRef(getAuthCookie("sessionToken") || "");
  const userIdRef = useRef(getAuthCookie("userId") || "");
  const token = tokenRef.current;
  const userId = userIdRef.current;

  /* ---------------- STATE ---------------- */
  const [selectedOrder, setSelectedOrder] = useState<OrderOption | null>(null);
  const [walletBalance, setWalletBalance] = useState<string>("0.00");
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [customerAddresses, setCustomerAddresses] = useState<AddressOption[]>([]);
  const [userData, setUserData] = useState<{
    firstName: string;
    lastName: string;
    phone1: string;
    email: string;
  } | null>(null);
  
  // Carrier and Package states
  const [carrierAccounts, setCarrierAccounts] = useState<CarrierAccount[]>([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>("");
  const [selectedShippingIntegrationId, setSelectedShippingIntegrationId] = useState<string>("");
  const [selectedCarrierAccountId, setSelectedCarrierAccountId] = useState<string>("");
  const [selectedCarrierName, setSelectedCarrierName] = useState<string>("");
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [selectedPreset, setSelectedPreset] = useState<PresetOption | null>(null);
  
  // Rates and Label states
  const [shippingRates, setShippingRates] = useState<Rate[]>([]);
  const [shipmentId, setShipmentId] = useState<string>("");
  const [loadingRates, setLoadingRates] = useState(false);
  const [buyingLabel, setBuyingLabel] = useState<string | null>(null); // rate_id being processed
  const [requestUuid, setRequestUuid] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [labelUrl, setLabelUrl] = useState<string>("");
  const [labelRecordId, setLabelRecordId] = useState<string>(""); // buy-label response _id
  const [selectedOrderId, setSelectedOrderId] = useState<string>(""); // Store order ID when order is selected
  const [downloadingLabel, setDownloadingLabel] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [selectedAddressOption, setSelectedAddressOption] = useState<AddressSelect2Option | null>(null);
  const [fromGoogleAddressLocked, setFromGoogleAddressLocked] = useState(false);
  const [toGoogleAddressLocked, setToGoogleAddressLocked] = useState(false);

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
    navigate(`${baseRoute}/buylabels`);
  }, [navigate, baseRoute]);
  
  // Flags to prevent duplicate API calls
  const balanceFetched = useRef(false);
  const warehousesFetched = useRef(false);
  const userDataFetched = useRef(false);

  /* ---------------- FETCH WALLET BALANCE (once on mount) ---------------- */
  useEffect(() => {
    if (balanceFetched.current || !token) return;
    balanceFetched.current = true;

    const fetchBalance = async () => {
      try {
        const data = await getWalletBalance(token);
        setWalletBalance(data.balance);
      } catch (err) {
        console.error("Failed to fetch wallet balance:", err);
      }
    };
    fetchBalance();
  }, [token]);

  /* ---------------- FETCH WAREHOUSES (once on mount) ---------------- */
  useEffect(() => {
    if (warehousesFetched.current || !token) return;
    warehousesFetched.current = true;

    const fetchWarehouses = async () => {
      setLoadingWarehouses(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/warehouses?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setWarehouses(data || []);
      } catch (err) {
        console.error("Error fetching warehouses:", err);
      } finally {
        setLoadingWarehouses(false);
      }
    };
    fetchWarehouses();
  }, [token]);

  /* ---------------- FETCH USER DATA (once on mount) ---------------- */
  useEffect(() => {
    if (userDataFetched.current || !token || !userId) return;
    userDataFetched.current = true;

    const fetchUser = async () => {
      try {
        const { fetchUserData } = await import("../../utils/userDataFetcher");
        const endUserData = await fetchUserData(userId, token);
        if (endUserData) {
          setUserData({
            firstName: endUserData.firstName || "",
            lastName: endUserData.lastName || "",
            phone1: endUserData.phone1 || "",
            email: endUserData.email || "",
          });
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    fetchUser();
  }, [token, userId]);

  /* ---------------- FETCH CARRIER ACCOUNTS ---------------- */
  useEffect(() => {
    if (!token || loadingCarriers) return;

    const fetchCarriers = async () => {
      setLoadingCarriers(true);
      try {
        // Fetch all carriers (shipping_integration_id is now optional)
        const accounts = await getCarrierAccounts(token, shipping_integration_id);
        setCarrierAccounts(accounts);
      } catch (err) {
        console.error("Failed to fetch carrier accounts:", err);
        showError("Failed to load carriers");
      } finally {
        setLoadingCarriers(false);
      }
    };

    fetchCarriers();
  }, [token, shipping_integration_id]);

  /* ---------------- LOAD PACKAGE OPTIONS (Select2) ---------------- */
  type PackageOption = { value: string; label: string; raw: ShippingPresetSelect2Result | null };
  type PackageGroup = { label: string; options: PackageOption[] };

  /* ---------------- LOAD PACKAGE TYPE OPTIONS (getPackageTemplatesSelect2 - package-templates/select2) ---------------- */
  const loadPackageOptions: LoadOptions<PackageOption, PackageGroup, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      if (!token || !selectedCarrierName) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const limit = 50;

      try {
        // Use getPackageTemplatesSelect2 for package templates
        const { results, more } = await getPackageTemplatesSelect2(token, {
          q: inputValue || "",
          page,
          limit,
          status: "active",
          carrier: selectedCarrierName,
        });

        const predefined: PackageOption[] = [
          { value: "own_packaging", label: "Own packaging", raw: null },
        ];
        const custom: PackageOption[] = [];

        for (const r of results) {
          const dim = r?.dimensions;
          const label = dim
            ? `${r.name || r.text} (${dim.length}in x ${dim.width}in x ${dim.height}in)`
            : r.name || r.text;
          const opt: PackageOption = {
            value: r.id,
            label,
            raw: {
              id: r.id,
              text: r.name || r.text,
              carrier: r.carrier_name,
              package: {
                length: dim?.length || 0,
                width: dim?.width || 0,
                height: dim?.height || 0,
                weight: dim?.weight || 0,
                dimension_unit: "in",
                weight_unit: "lb",
              },
              is_global: true,
            } as ShippingPresetSelect2Result,
          };
          // All package templates are treated as predefined
          predefined.push(opt);
        }

        const groups: PackageGroup[] = [];
        if (predefined.length) groups.push({ label: "Predefined", options: predefined });
        if (custom.length) groups.push({ label: "Custom Presets", options: custom });

        return { options: groups as any, hasMore: more, additional: { page: page + 1 } };
      } catch (e) {
        console.error(e);
        showError("Failed to load packages");
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
    },
    [token, selectedCarrierName]
  );

  // Convert warehouses to dropdown options (memoized)
  const warehouseOptions = useMemo(() => 
    warehouses.map((wh) => ({
      value: wh._id,
      label: wh.label,
    })),
    [warehouses]
  );

  // Convert carrier accounts to dropdown options (memoized)
  const carrierOptions = useMemo(() => 
    carrierAccounts.map((acc) => ({
      value: acc._id,
      label: acc.carrier_name,
    })),
    [carrierAccounts]
  );

  /* ---------------- FORMIK ---------------- */
  const formik = useFormik({
    initialValues: {
      orderNumber: "",
      markShipped: false,

      warehouse: "",

      customer: "",
      customerAddress: "",

      fromFirstName: "",
      fromLastName: "",
      fromPhone: "",
      fromEmail: "",
      fromStreet: "",
      fromAddress2: "",
      fromCity: "",
      fromState: "",
      fromCountry: "US",
      fromZip: "",
      skipValidation: false,

      toFirstName: "",
      toLastName: "",
      toPhone: "",
      toEmail: "",
      toStreet: "",
      toAddress2: "",
      toCity: "",
      toState: "",
      toCountry: "US",
      toZip: "",

      carrier: "",
      packageType: "",
      length: "",
      width: "",
      height: "",
      weight: "",

      addInsurance: false,
      insuranceAmount: "",
    },
    onSubmit: (values) => {
      console.log("Form submitted:", values);
      // TODO: Submit to API
    },
  });

  const clearFromAddressFields = () => {
    formik.setFieldValue("fromCity", "");
    formik.setFieldValue("fromState", "");
    formik.setFieldValue("fromCountry", US_COUNTRY_CODE);
    formik.setFieldValue("fromZip", "");
  };

  const clearToAddressFields = () => {
    formik.setFieldValue("toCity", "");
    formik.setFieldValue("toState", "");
    formik.setFieldValue("toCountry", US_COUNTRY_CODE);
    formik.setFieldValue("toZip", "");
  };

  const handleFromStreetChange = (value: string) => {
    formik.setFieldValue("fromStreet", value);
    if (fromGoogleAddressLocked || !value.trim()) {
      clearFromAddressFields();
      setFromGoogleAddressLocked(false);
    } else if (!formik.values.fromCountry) {
      formik.setFieldValue("fromCountry", US_COUNTRY_CODE);
    }
  };

  const handleToStreetChange = (value: string) => {
    formik.setFieldValue("toStreet", value);
    if (toGoogleAddressLocked || !value.trim()) {
      clearToAddressFields();
      setToGoogleAddressLocked(false);
    } else if (!formik.values.toCountry) {
      formik.setFieldValue("toCountry", US_COUNTRY_CODE);
    }
  };

  const selectedFromStateCode =
    getUsStateByCode(formik.values.fromState)?.value ||
    getUsStateByName(formik.values.fromState)?.value ||
    "";

  const selectedToStateCode =
    getUsStateByCode(formik.values.toState)?.value ||
    getUsStateByName(formik.values.toState)?.value ||
    "";

  const handleFromStateChange = (value: string) => {
    const selectedState = getUsStateByCode(value);
    formik.setFieldValue("fromState", selectedState?.value || "");
  };

  const handleToStateChange = (value: string) => {
    const selectedState = getUsStateByCode(value);
    formik.setFieldValue("toState", selectedState?.value || "");
  };

  /* ---------------- PRE-FILL FROM ADDRESS WITH USER DATA ---------------- */
  const userDataApplied = useRef(false);
  useEffect(() => {
    if (userDataApplied.current || !userData) return;
    userDataApplied.current = true;

    // Pre-fill From Address fields with logged-in user data
    formik.setFieldValue("fromFirstName", userData.firstName || "");
    formik.setFieldValue("fromLastName", userData.lastName || "");
    formik.setFieldValue("fromPhone", userData.phone1 || "");
    formik.setFieldValue("fromEmail", userData.email || "");
  }, [userData]);

  /* ---------------- LOAD ORDER OPTIONS (AsyncPaginate) ---------------- */
  const loadOrderOptions: LoadOptions<OrderOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      try {
        const response = await searchShippingOrders(token, userId, inputValue, page);
        // console.log("Search Orders Response:", response);

        const options: OrderOption[] = response.results.map((order) => ({
          value: order.id,
          label: `#${order.order_number} - ${order.customer_name}`,
          raw: order,
        }));
        // console.log("Order Options:", options); 

        return {
          options,
          hasMore: response.pagination?.more ?? false,
          additional: { page: page + 1 },
        };
      } catch (err) {
        console.error("Failed to load orders:", err);
        showError("Failed to search orders");
        return { options: [], hasMore: false };
      }
    },
    [token, userId]
  );

  /* ---------------- LOAD PRESET OPTIONS (getCarrierPackageDetails - shipping-presets/select2) ---------------- */
  const loadPresetOptions: LoadOptions<PresetOption, any, { page: number }> = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      if (!token) return { options: [], hasMore: false, additional: { page } };
      try {
        // Use getCarrierPackageDetails - if carrier is selected, filter by it; otherwise show all
        const carrier = selectedCarrierName || "";
        const { results, more } = await getCarrierPackageDetails(
          token,
          carrier,
          inputValue || "",
          page,
          10
        );

        const options: PresetOption[] = results.map((r) => ({
          value: r.id,
          label: r.name || r.text,
          raw: r,
        }));

        return { options, hasMore: more, additional: { page: page + 1 } };
      } catch (e) {
        console.error(e);
        showError("Failed to load presets");
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, selectedCarrierName]
  );

  /* ---------------- LOAD CUSTOMER OPTIONS (customer-groups, Select2 page-change logic same as SignageCreateOrder Select Agent) ---------------- */
  const CUSTOMER_PAGE_SIZE = 20;
  const loadCustomerOptions: LoadOptions<CustomerOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      if (!token || !userId) return { options: [], hasMore: false, additional: { page: 1 } };
      try {
        const { results, more } = await getCustomerGroupUsers(
          token,
          userId,
          (inputValue || "").trim(),
          page,
          CUSTOMER_PAGE_SIZE
        );
        const options: CustomerOption[] = results.map((u) => ({
          value: u.id,
          label: u.name,
          name: u.name,
          email: u.email,
          raw: u,
        }));
        // Page-change logic from SignageCreateOrder Select Agent: next page sent as additional so scroll loads more
        return {
          options,
          hasMore: more,
          additional: { page: page + 1 },
        };
      } catch (e) {
        console.error(e);
        showError("Failed to load customers");
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, userId]
  );

  /* ---------------- LOAD ADDRESS OPTIONS (addresses/all/select2 - selected customer id + that customer's license_type from API) ---------------- */
  const ADDRESS_PAGE_SIZE = 10;
  const selectedCustomerLicenseType = selectedCustomer?.raw?.license_type ?? "";

  const loadAddressOptions: LoadOptions<AddressSelect2Option, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const selectedUserId = selectedCustomer?.value;
      const licenseType = selectedCustomer?.raw?.license_type ?? "";
      if (!token || !selectedUserId || !licenseType) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      try {
        const { results, more } = await getAddressesAllSelect2(
          token,
          selectedUserId,
          licenseType,
          (inputValue || "").trim(),
          page,
          ADDRESS_PAGE_SIZE
        );
        const options: AddressSelect2Option[] = results.map((r) => ({
          value: r.id,
          label: r.text,
          raw: r,
        }));
        return { options, hasMore: more, additional: { page: page + 1 } };
      } catch (e) {
        console.error(e);
        showError("Failed to load addresses");
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, selectedCustomer?.value, selectedCustomerLicenseType]
  );

  /* ---------------- FILL TO ADDRESS FROM ADDRESS ID (last-selected address in UI) ---------------- */
  /* Only updates address-related fields so customer/address dropdown selections stay unchanged */
  const fillToAddressFromAddressId = useCallback(
    async (addressId: string) => {
      if (!token || !addressId) return;
      try {
        const addr = await getAddressById(token, addressId);
        const details = addr?.address_details ?? {};
        formik.setFieldValue("customerAddress", addressId);
        formik.setFieldValue("toFirstName", addr?.first_name ?? "");
        formik.setFieldValue("toLastName", addr?.last_name ?? "");
        formik.setFieldValue("toPhone", details?.phone ?? "");
        if (addr?.email != null) formik.setFieldValue("toEmail", addr.email);
        formik.setFieldValue("toStreet", details?.addressLine1 ?? "");
        formik.setFieldValue("toAddress2", details?.addressLine2 ?? "");
        formik.setFieldValue("toCity", details?.city ?? "");
        formik.setFieldValue("toState", details?.stateCode ?? details?.state ?? "");
        formik.setFieldValue("toCountry", details?.countryCode ?? "US");
        formik.setFieldValue("toZip", details?.zip ?? "");
        setToGoogleAddressLocked(false);
      } catch (e) {
        console.error(e);
        showError("Failed to load address details");
      }
    },
    [token]
  );

  /* ---------------- HANDLE ORDER SELECTION ---------------- */
  const handleOrderSelect = async (option: OrderOption | null) => {
    setSelectedOrder(option);
    setSelectedCustomer(null);
    setSelectedAddressOption(null);
    setToGoogleAddressLocked(false);

    if (option?.raw) {
      const order = option.raw;
      const shippingAddr = order.shipping_address;
      const addrDetails = shippingAddr?.address_details;

      // Store order ID for buy-label API call
      setSelectedOrderId(order.id || "");

      // Generate request_uuid when order is selected
      const uuid = await ensureDraftUniqueId(token);
      if (uuid) {
        setRequestUuid(uuid);
      }

      // Build address options from shipping address
      if (shippingAddr) {
        const addressLabel = shippingAddr.label || addrDetails?.label || "Shipping Address";
        setCustomerAddresses([
          { value: shippingAddr._id, label: addressLabel },
        ]);
      }

      // Use setValues to batch all updates at once (prevents multiple re-renders)
      formik.setValues({
        ...formik.values,
        orderNumber: order.order_number || "",
        customer: order.customer_name || "",
        customerAddress: shippingAddr?._id || "",
        // toFirstName: shippingAddr?.first_name || "",
        toFirstName: order?.customer_first_name || "",
        toLastName: order?.customer_last_name || "",
        // toLastName: shippingAddr?.last_name || "",
        toPhone: addrDetails?.phone || "",
        toEmail: order.customer_email || "",
        toStreet: addrDetails?.addressLine1 || "",
        toAddress2: addrDetails?.addressLine2 || "",
        toCity: addrDetails?.city || "",
        toState: addrDetails?.stateCode || addrDetails?.state || "",
        toCountry: addrDetails?.countryCode || "US",
        toZip: addrDetails?.zip || "",
      });
    } else {
      setSelectedOrderId("");
      setCustomerAddresses([]);
      setSelectedCustomer(null);
      setSelectedAddressOption(null);

      // Clear To Address if no order selected - batch update
      formik.setValues({
        ...formik.values,
        orderNumber: "",
        customer: "",
        customerAddress: "",
        toFirstName: "",
        toLastName: "",
        toPhone: "",
        toEmail: "",
        toStreet: "",
        toAddress2: "",
        toCity: "",
        toState: "",
        toZip: "",
      });
    }
  };

  /* ---------------- HANDLE WAREHOUSE SELECTION ---------------- */
  const handleWarehouseChange = async (value: string) => {
    setFromGoogleAddressLocked(false);
    // Generate request_uuid when warehouse is selected (same as order selection)
    const uuid = await ensureDraftUniqueId(token);
    if (uuid) {
      setRequestUuid(uuid);
    }

    // Find selected warehouse and fill From Address (only address fields, NOT user personal info)
    const selectedWarehouse = warehouses.find((wh) => wh._id === value);

    if (selectedWarehouse?.address) {
      const addr = selectedWarehouse.address;
      // Only update warehouse/from-address fields (never spread formik.values — avoids stale overwrites)
      formik.setFieldValue("warehouse", value);
      formik.setFieldValue("fromStreet", addr.addressLine1 || "");
      formik.setFieldValue("fromAddress2", addr.addressLine2 || "");
      formik.setFieldValue("fromCity", addr.city || "");
      formik.setFieldValue("fromState", addr.stateCode || addr.state || "");
      formik.setFieldValue("fromCountry", normalizeCountryCode(addr.country || "US"));
      formik.setFieldValue("fromZip", addr.zip || "");
    } else {
      formik.setFieldValue("warehouse", value);
    }
  };

  /* ---------------- HANDLE CARRIER SELECTION ---------------- */
  const handleCarrierChange = (value: string, opts?: { keepPreset?: boolean }) => {
    const selectedCarrier = carrierAccounts.find((acc) => acc._id === value);
    // console.log("Selected Carrier:", selectedCarrier);  
    // console.log("Carrier Accounts:", carrierAccounts);
    // console.log("Selected Carrier ID:", selectedCarrier?.carrier_account_id);
    
    if (selectedCarrier) {
      setSelectedCarrierId(value);
      // shipping_integration_id may not be in carrier account response, will get it from package details
      setSelectedShippingIntegrationId(selectedCarrier.shipping_integration_id || "");
      setSelectedCarrierAccountId(selectedCarrier.carrier_account_id);
      setSelectedCarrierName(selectedCarrier.carrier_name);
      
      // Update formik carrier field
      formik.setFieldValue("carrier", selectedCarrier.carrier_name);
      
      // Reset package selection and dimensions (skip when preset flow will re-apply them)
      if (!opts?.keepPreset) {
        formik.setFieldValue("packageType", "");
        formik.setFieldValue("length", "");
        formik.setFieldValue("width", "");
        formik.setFieldValue("height", "");
        formik.setFieldValue("weight", "");
        setSelectedPackage(null);
        setSelectedPreset(null);
      }
    } else {
      setSelectedCarrierId("");
      setSelectedShippingIntegrationId("");
      setSelectedCarrierAccountId("");
      setSelectedCarrierName("");
      formik.setFieldValue("carrier", "");
      setSelectedPackage(null);
      if (!opts?.keepPreset) setSelectedPreset(null);
    }
  };

  useEffect(() => {
   // Refresh wallet balance
     const handleGetRatess = async () => {
      try {
        const balanceData = await getWalletBalance(token);
        // console.log("Refreshed wallet balance:", balanceData.balance);
        setWalletBalance(balanceData.balance);
      } catch (err) {
        console.error("Failed to refresh wallet balance:", err);
      }
    }
  }, []); // Add dependencies as needed

  // Sync L/W/H when package dropdown value is set (preset auto-fill or manual select)
  useEffect(() => {
    if (!selectedPackage || selectedPackage.value === "own_packaging") return;
    const dim = extractPackageDimensions(selectedPackage.raw);
    if (!dim) return;
    formik.setFieldValue("length", dimFieldString(dim.length));
    formik.setFieldValue("width", dimFieldString(dim.width));
    formik.setFieldValue("height", dimFieldString(dim.height));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage]);

  /* ---------------- GET DISCOUNTED RATES ---------------- */
  const handleGetRates = async () => {
    if (!token  || !selectedCarrierAccountId) {
      showError("Please select a carrier first");
      return;
    }

    // Validate required fields
    if (!formik.values.fromFirstName || !formik.values.fromStreet || !formik.values.fromCity || 
        !formik.values.fromState || !formik.values.fromZip || !formik.values.fromCountry) {
      showError("Please fill in all required From Address fields");
      return;
    }

    if (!formik.values.toFirstName || !formik.values.toStreet || !formik.values.toCity || 
        !formik.values.toState || !formik.values.toZip || !formik.values.toCountry) {
      showError("Please fill in all required To Address fields");
      return;
    }

    if (!formik.values.length || !formik.values.width || !formik.values.height || !formik.values.weight) {
      showError("Please fill in all package dimensions");
      return;
    }

    setLoadingRates(true);
    try {
      // Calculate insurance amount - default to "0.00" if not entered
      const insuranceAmount = formik.values.addInsurance && formik.values.insuranceAmount 
        ? parseFloat(formik.values.insuranceAmount).toFixed(2) 
        : "0.00";

      const payload = {
        shipping_integration_id: selectedShippingIntegrationId,
        carrier_account_id: selectedCarrierAccountId,
        ship_without_address_validation: formik.values.skipValidation || false,
        label_format: "PDF",
        label_layout: "4x6",
        insurance: insuranceAmount,
        from_address: {
          name: `${formik.values.fromFirstName} ${formik.values.fromLastName}`.trim(),
          company: "RealEsy",
          phone: formik.values.fromPhone || "",
          email: formik.values.fromEmail || "",
          address_line1: formik.values.fromStreet || "",
          address_line2: formik.values.fromAddress2 || "",
          city: formik.values.fromCity || "",
          city_code: "", // Keep empty as per API requirement
          state: (formik.values.fromState || ""),
          state_code:(formik.values.fromState || ""),
          country: getCountryName(normalizeCountryCode(formik.values.fromCountry || "US")),
          country_code: normalizeCountryCode(formik.values.fromCountry || "US"),
          postal_code: formik.values.fromZip || "",
        },
        to_address: {
          name: `${formik.values.toFirstName} ${formik.values.toLastName}`.trim(),
          company: "",
          phone: formik.values.toPhone || "",
          email: formik.values.toEmail || "",
          address_line1: formik.values.toStreet || "",
          address_line2: formik.values.toAddress2 || "",
          city: formik.values.toCity || "",
          city_code: "", // Keep empty as per API requirement
          state: (formik.values.toState || ""),
          state_code:(formik.values.toState || ""),
          country: getCountryName(normalizeCountryCode(formik.values.toCountry || "US")),
          country_code: normalizeCountryCode(formik.values.toCountry || "US"),
          postal_code: formik.values.toZip || "",
        },
        parcel: {
          length: parseFloat(formik.values.length) || 1,
          width: parseFloat(formik.values.width) || 1,
          height: parseFloat(formik.values.height) || 1,
          weight: parseFloat(formik.values.weight) || 1,
          weight_unit: "lb",
        },
      };

      const response = await getShippingRates(token, payload);
      setShippingRates(response.rates || []);
      setShipmentId(response.shipment_id || "");
    } catch (err) {
      console.error("Failed to fetch rates:", err);
      showError(err instanceof Error ? err.message : "Failed to fetch shipping rates");
      setShippingRates([]);
      setShipmentId("");
    } finally {
      setLoadingRates(false);
    }
  };

  /* ---------------- BUY LABEL ---------------- */
  const handleBuyLabel = useCallback(async (rate: Rate) => {
    if (!token || !shipmentId || !requestUuid) {
      // console.log("Missing required information for buying label:", { token, shipmentId, requestUuid });
      showError("Missing required information. Please try getting rates again.");
      return;
    }

    setBuyingLabel(rate.id);
    try {
      const payload = {
        // shipping_integration_id: selectedShippingIntegrationId,
        shipment_id: shipmentId,
        rate_id: rate.id,
        request_uuid: requestUuid,
        ship_without_address_validation: formik.values.skipValidation || false,
        // label_format: "PDF",
        // label_layout: "4x6",
        insurance: formik.values.addInsurance && formik.values.insuranceAmount 
          ? parseFloat(formik.values.insuranceAmount).toFixed(2) 
          : "0.00",
        // Include order number and order ID if order is selected
        order_id: formik.values.orderNumber || undefined,
        order_database_id: selectedOrderId || undefined,
      };

      const response = await buyShippingLabel(token, payload);
      
      // Remove request_uuid from localStorage after successful buy
      localStorage.removeItem(UNIQUE_ID_KEY);
      setRequestUuid(null);

      // Store label record id for download endpoint
      const respAny: any = response as any;
      const labelId = respAny?._id?.$oid || respAny?._id || "";
      setLabelRecordId(String(labelId));

      // If "Mark this order as shipped" is checked and order ID is available, call shipped API
      if (formik.values.markShipped && selectedOrderId) {
        try {
          await markOrderAsShipped(token, selectedOrderId);

          // After shipped success: fetch latest order + send shipped-with-tracking email.
          // try {
          //   const latestOrder = await getOrderById(token, selectedOrderId);
          //   const trackingNumber = String((response as any)?.tracking_code || "").trim();
          //   if (trackingNumber) {
          //     const endUserId = latestOrder.agent_id && latestOrder.customer_email
          //       ? latestOrder.agent_id
          //       : latestOrder.inventory_source === "office" && latestOrder.team_member_id
          //         ? latestOrder.team_member_id
          //         : latestOrder.brokerage_firm_id || "";
          //     if (endUserId) {
          //       const { fetchUserData } = await import("../../utils/userDataFetcher");
          //       const endUserData = await fetchUserData(endUserId, token);
          //       const endUserEmail = String(endUserData?.email || "").trim();
          //       if (endUserEmail) {
          //         let emailLicenseType: "AGENT" | "BROKERAGE_TEAM_MEMBER" | "BROKERAGE_FIRM" = "AGENT";
          //         if (
          //           endUserData?.licenseType === "BROKERAGE_FIRM" ||
          //           endUserData?.licenseType === "BROKERAGE_TEAM_MEMBER"
          //         ) {
          //           emailLicenseType =
          //             endUserData.licenseType === "BROKERAGE_TEAM_MEMBER"
          //               ? "BROKERAGE_TEAM_MEMBER"
          //               : "BROKERAGE_FIRM";
          //         }

          //         const firstName =
          //           String(endUserData?.firstName || "").trim() ||
          //           endUserEmail.split("@")[0] ||
          //           "User";
          //         const orderIdLabel = `#${latestOrder.order_number || latestOrder.id || latestOrder._id || "ORDER"}`;
          //         const shippedOn = new Date().toLocaleDateString("en-US", {
          //           month: "2-digit",
          //           day: "2-digit",
          //           year: "numeric",
          //         });
          //         const ad = latestOrder.shipping_address?.address_details;
          //         const shippingAddress = ad
          //           ? `${ad.addressLine1 || ""}, ${ad.addressLine2 || ""}, ${ad.city || ""}, ${ad.state || ""} ${ad.zip || ""}`
          //               .replace(/,\s*,/g, ",")
          //               .replace(/^,\s*|,\s*$/g, "")
          //               .trim()
          //           : "N/A";
          //         const items = Array.isArray(latestOrder.items) ? latestOrder.items : [];
          //         const productsIncluded = items.length
          //           ? items
          //               .map((item: any) => {
          //                 const qty = Number(item?.quantity || 1);
          //                 const name = String(item?.product_name || item?.name || "Product");
          //                 return `${name} (Qty: ${qty})`;
          //               })
          //               .join(", ")
          //           : "N/A";
          //         const lineItems = items.length
          //           ? `<h3 class="email-line-items-heading" style="margin:0 0 16px 0;color:#111827;font-size:18px;font-weight:500;font-family:'Roboto',Arial,sans-serif;">📦 Line Items</h3><div class="email-line-items-container" style="background-color:#ffffff;border-radius:8px;padding:25px;margin:0 0 32px 0;border:1px solid #e5e7eb;overflow:hidden;">${items
          //               .map((item: any, idx: number) => {
          //                 const qty = Number(item?.quantity || 1);
          //                 const name = String(item?.product_name || item?.name || "Product");
          //                 return `<div style="padding:${idx === 0 ? "0" : "16px"} 0 8px 0;border-top:${idx === 0 ? "none" : "1px solid #e5e7eb"};"><table width="100%" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;"><tr><td style="padding:0;vertical-align:top;"><div style="font-weight:500;color:#111827;font-size:14px;line-height:20px;">${name}</div></td><td style="padding:0;vertical-align:top;text-align:right;white-space:nowrap;"><div style="color:#6b7280;font-size:12px;line-height:18px;font-weight:500;">Qty: ${qty}</div></td></tr></table></div>`;
          //               })
          //               .join("")}</div>`
          //           : "";

          //         const carrierName =
          //           String(respAny?.carrier || rate.carrier || "").trim() ||
          //           String((latestOrder as any)?.shipping_method?.carrier_name || "").trim() ||
          //           "N/A";

          //         await sendTriggerEmails(
          //           "Signage Sales order shipped with tracking",
          //           emailLicenseType,
          //           {
          //             firstName,
          //             orderId: orderIdLabel,
          //             vendorName: String(latestOrder.signage_company_name || "Signage Company"),
          //             signageCompanyName: String(latestOrder.signage_company_name || "Signage Company"),
          //             carrierName,
          //             trackingNumber,
          //             trackingNumberHtml: formatTrackingNumberEmailHtml(trackingNumber),
          //             shippedOn,
          //             shippingAddress,
          //             productsIncluded,
          //             lineItems,
          //             thanksName: String(latestOrder.signage_company_name || "RealEsy"),
          //           },
          //           endUserEmail,
          //           process.env.REACT_APP_FROM_EMAIL2 || "",
          //           token
          //         );
          //       }
          //     }
          //   }
          // } catch (emailErr) {
          //   console.warn("Failed to send shipped-with-tracking email from Buy Label:", emailErr);
          // }

          showSuccess("Order marked as shipped successfully!");
        } catch (shippedErr) {
          console.error("Failed to mark order as shipped:", shippedErr);
          // Don't fail the whole operation if shipped call fails, just log it
          showError("Label created successfully, but failed to mark order as shipped");
        }
      }

      // Show success modal with label URL
      setLabelUrl(response.label_url || "");
      setShowSuccessModal(true);

      // Refresh wallet balance
      try {
        const balanceData = await getWalletBalance(token);
        // console.log("Refreshed wallet balance:", balanceData.balance);
        setWalletBalance(balanceData.balance);
      } catch (err) {
        console.error("Failed to refresh wallet balance:", err);
      }
    } catch (err) {
      console.error("Failed to buy label:", err);
      showError(err instanceof Error ? err.message : "Failed to buy shipping label");
    } finally {
      setBuyingLabel(null);
    }
  }, [token, shipmentId, requestUuid, selectedShippingIntegrationId, selectedOrderId, formik.values.skipValidation, formik.values.addInsurance, formik.values.insuranceAmount, formik.values.markShipped, formik.values.orderNumber]);

  /* ---------------- DOWNLOAD LABEL (API stream) ---------------- */
  const handleDownloadLabel = useCallback(async () => {
    if (!token) return;
    if (!labelRecordId) {
      showError("Label id not found for download");
      return;
    }
    setDownloadingLabel(true);
    try {
      const { blob, filename } = await downloadEasyPostLabelPdf(token, labelRecordId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `label_${labelRecordId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showError(e instanceof Error ? e.message : "Failed to download label");
    } finally {
      setDownloadingLabel(false);
    }
  }, [token, labelRecordId]);
  

  /* ---------------- SHIPPING RATES TABLE ---------------- */
  // Convert rates to table rows
  const ratesTableData = useMemo<ShippingRateRow[]>(() => {
    return shippingRates.map((rate) => ({
      carrierType: rate.carrier,
      service: rate.est_delivery_days 
        ? `${rate.service} (${rate.est_delivery_days} business days)`
        : rate.service,
      rate: rate.rate,
      currency: rate.currency,
      insurance_value: rate.insurance_value || "0.00",
      insurance_fee_estimate: rate.insurance_fee_estimate || "0.00",
      rate_total: (
        (Number.parseFloat(rate.rate || "0") || 0) +
        (Number.parseFloat(rate.insurance_fee_estimate || "0") || 0)
      ).toFixed(2),
      rateId: rate.id, // Store rate ID for buy label action
    }));
  }, [shippingRates]);

  // Update column helper to include action button with proper styling
  const ratesColumns = useMemo(() => [
    columnHelper.accessor("carrierType", {
      header: () => <div className="th-text text-start fw-bold" style={{ textTransform: "uppercase" }}>CARRIER TYPE</div>,
      cell: (info) => <div className="text-sm text-start">{info.getValue()}</div>,
    }),
    columnHelper.accessor("service", {
      header: () => <div className="th-text text-start fw-bold" style={{ textTransform: "uppercase" }}>SERVICE & EST DELIVERY DAYS</div>,
      cell: (info) => <div className="text-sm text-start">{info.getValue()}</div>,
    }),
    columnHelper.accessor("rate", {
      header: () => <div className="th-text text-end fw-bold" style={{ textTransform: "uppercase" }}>RATE</div>,
      cell: (info) => <div className="text-sm text-end">${info.getValue()}</div>,
    }),
    // columnHelper.accessor("currency", {
    //   header: () => <div className="th-text text-center">Currency</div>,
    //   cell: (info) => <div className="text-sm text-center">{info.getValue()}</div>,
    // }),
    // columnHelper.accessor("insurance_value", {
    //   header: () => <div className="th-text text-center">Insurance Value</div>,
    //   cell: (info) => <div className="text-sm text-center">${info.getValue() || "0.00"}</div>,
    // }),
    // columnHelper.accessor("insurance_fee_estimate", {
    //   header: () => <div className="th-text text-center">Insurance Fee Estimate</div>,
    //   cell: (info) => <div className="text-sm text-center">${info.getValue() || "0.00"}</div>,
    // }),
    // columnHelper.accessor("rate_total", {
    //   header: () => <div className="th-text text-center">Rate Total</div>,
    //   cell: (info) => <div className="text-sm text-center">${info.getValue() || "0.00"}</div>,
    // }),
    columnHelper.display({
      id: "actions",
      header: () => <div className="th-text text-center fw-bold" style={{ textTransform: "uppercase" }}>ACTION</div>,
      cell: (info) => {
        const rowData = info.row.original;
        const rate = shippingRates.find((r) => r.id === rowData.rateId);
        if (!rate) return null;
        return (
          <div className="d-flex align-items-center justify-content-center">
            <button
              className="btn buy-label-btn fw-medium fs-15 br-8"
              onClick={() => handleBuyLabel(rate)}
              disabled={buyingLabel === rate.id || !shipmentId}
            >
              {buyingLabel === rate.id ? "Processing..." : "Buy Label"}
            </button>
          </div>
        );
      },
    }),
  ], [shippingRates, buyingLabel, shipmentId, handleBuyLabel]);

  // Memoize getCoreRowModel to prevent infinite re-renders
  const coreRowModel = useMemo(() => getCoreRowModel<ShippingRateRow>(), []);
  
  const shippingRatesTable = useReactTable({
    data: ratesTableData,
    columns: ratesColumns,
    getCoreRowModel: coreRowModel,
  });

  return (
    <div className="content container">

      {/* ================= HEADER CARD ================= */}
      <div className="card box-shadow-datatables shadow-border-2 mb-4 p-4">
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="mb-0 fw-bold">Buy Label</h2>
          <span className="fs-16">
            Current Balance: <span className="fw-semibold" style={{ color: "#00a63e" }}>${walletBalance}</span>
          </span>
        </div>
      </div>

      {/* ================= SEARCH ORDER ================= */}
      <div className="card box-shadow-datatables shadow-border-2 mb-4">
        <div className="pt-4 px-4">
          <div className="row g-3 align-items-start mb-4">
            {/* Search Order (right) */}
            <div className="col-md-6 text-start">
              <label className="form-label fw-semibold">Search Order</label>
              <AsyncPaginate
                classNamePrefix="rs"
                placeholder="Enter order number"
                value={selectedOrder}
                loadOptions={loadOrderOptions}
                onChange={handleOrderSelect}
                additional={{ page: 1 }}
                debounceTimeout={300}
                isClearable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: "38px",
                    border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
                    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
                  }),
                }}
              />
              <div className="mt-2 d-flex justify-content-start">
                <UICheckbox
                  formik={formik}
                  name="markShipped"
                  label="Mark this order as shipped"
                />
              </div>
            </div>
             {/* Select preset (left) */}
             <div className="col-md-6 text-start">
              <label className="form-label fw-semibold">Select preset</label>
              <AsyncPaginate
                classNamePrefix="rs"
                placeholder="Search preset..."
                value={selectedPreset}
                loadOptions={loadPresetOptions}
                onChange={async (opt) => {
                  setSelectedPreset(opt as any);
                  if (!opt) return;
                  const raw = (opt as any).raw as ShippingPresetSelect2Result;

                  // 1) set carrier based on preset carrier (shipping-presets response)
                  const carrierAcc = carrierAccounts.find(
                    (c) =>
                      (c.carrier_name || "").toLowerCase() === (raw.carrier || "").toLowerCase()
                  );
                  if (carrierAcc) {
                    handleCarrierChange(carrierAcc._id, { keepPreset: true });
                  }

                  // 2) set warehouse from preset if available, otherwise default/first
                  const presetWhId = raw.ship_from_warehouse_id;
                  const defaultWhId =
                    (presetWhId && warehouses.find((w) => w._id === presetWhId)?._id) ||
                    (warehouses as any[]).find((w) => w?.is_default)?.["_id"] ||
                    (warehouses[0]?._id || "");
                  if (defaultWhId) {
                    await handleWarehouseChange(defaultWhId);
                  }

                  // 3) set package type and dimensions from preset (L/W/H only; weight stays empty)
                  const presetPackageName = raw.name || raw.text || "";
                  formik.setFieldValue("packageType", presetPackageName);
                  const dim = extractPackageDimensions(raw);
                  if (dim) {
                    formik.setFieldValue("length", dimFieldString(dim.length));
                    formik.setFieldValue("width", dimFieldString(dim.width));
                    formik.setFieldValue("height", dimFieldString(dim.height));
                  } else {
                    formik.setFieldValue("length", "");
                    formik.setFieldValue("width", "");
                    formik.setFieldValue("height", "");
                  }
                  formik.setFieldValue("weight", "");

                  // 4) Set Package Type dropdown directly from preset data (no API call)
                  if (dim) {
                    const label = `${presetPackageName} (${dim.length}in x ${dim.width}in x ${dim.height}in)`;
                    const packageOption: PackageOption = {
                      value: raw.id || "preset-package",
                      label,
                      raw: {
                        id: raw.id,
                        text: presetPackageName,
                        carrier: raw.carrier,
                        package: {
                          length: Number(dim.length) || 0,
                          width: Number(dim.width) || 0,
                          height: Number(dim.height) || 0,
                          weight: Number(dim.weight) || 0,
                          dimension_unit: "in",
                          weight_unit: "lb",
                        },
                        is_global: raw.is_global || false,
                      } as ShippingPresetSelect2Result,
                    };
                    setSelectedPackage(packageOption);
                  } else {
                    setSelectedPackage(null);
                  }
                }}
                additional={{ page: 1 }}
                debounceTimeout={300}
                isClearable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: "38px",
                    border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
                    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
                  }),
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
               <div className="mt-2 d-flex justify-content-start">
               Auto-fills from address, carrier & dimensions
              </div>
            </div>
          </div>
          <hr/>
        </div>

        {/* ================= ADDRESSES ================= */}
        <div className="row g-4 mb-2 p-3">

          {/* ---------- FROM ADDRESS ---------- */}
          <div className="col-md-6">
            <div className="card box-shadow-datatables shadow-border-2 h-100">
              <div className="card-body">
                <h6 className="fw-semibold mb-3">From Address</h6>

                <div className="mb-3">
                  <label className="form-label">Warehouse</label>
                  <select
                    className="form-select"
                    value={formik.values.warehouse}
                    onChange={(e) => handleWarehouseChange(e.target.value)}
                    disabled={loadingWarehouses}
                  >
                    <option value="">
                      {loadingWarehouses ? "Loading..." : "Select Warehouse"}
                    </option>
                    {warehouseOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <UIInput formik={formik} name="fromFirstName" label="First Name" required />
                  </div>
                  <div className="col-md-6">
                    <UIInput formik={formik} name="fromLastName" label="Last Name" required />
                  </div>

                  <div className="col-md-6">
                    <UIInput formik={formik} name="fromPhone" label="Phone" required />
                  </div>
                  <div className="col-md-6">
                    <UIInput formik={formik} name="fromEmail" label="Email" required />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Street Address <span className="text-danger">*</span>
                  </label>
                  <AddressAutocomplete
                    id="fromStreet"
                    name="fromStreet"
                    value={formik.values.fromStreet}
                    onChange={handleFromStreetChange}
                    onSelect={(picked) => {
                      formik.setValues({
                        ...formik.values,
                        fromStreet: picked.addressLine1 || "",
                        fromCity: picked.city || "",
                        fromState: picked.stateCode || picked.state || "",
                        fromZip: picked.zipcode || "",
                        fromCountry: picked.countryCode || US_COUNTRY_CODE,
                      });
                      setFromGoogleAddressLocked(true);
                    }}
                    className="form-control table-cells br-8"
                    placeholder="Enter address"
                    autoComplete="new-password"
                  />
                </div>
                <UIInput formik={formik} name="fromAddress2" label="Address 2" />
                <UIInputDisable
                  formik={formik}
                  name="fromCity"
                  label="City"
                  required
                  disabled={fromGoogleAddressLocked}
                  autocomplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                />

                <div className="mb-3">
                  <label className="form-label">
                    State <span className="text-danger">*</span>
                  </label>
                  {fromGoogleAddressLocked ? (
                    <input
                      type="text"
                      className="form-control"
                      name="fromState"
                      placeholder="State"
                      value={formik.values.fromState}
                      disabled
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  ) : (
                    <select
                      className="form-select"
                      name="fromState"
                      value={selectedFromStateCode}
                      onChange={(e) => handleFromStateChange(e.target.value)}
                      onBlur={formik.handleBlur}
                      autoComplete="new-password"
                    >
                      <option value="">-- Select State --</option>
                      {US_STATES.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <UIInputDisable
                  formik={formik}
                  name="fromCountry"
                  label="Country"
                  required
                  disabled
                  autocomplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                />

                <UIInputDisable
                  formik={formik}
                  name="fromZip"
                  label="Zip Code"
                  required
                  disabled={fromGoogleAddressLocked}
                  autocomplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                />

                <UICheckbox
                  formik={formik}
                  name="skipValidation"
                  label="Ship without address validation"
                />
              </div>
            </div>
          </div>

          {/* ---------- TO ADDRESS ---------- */}
          <div className="col-md-6">
            <div className="card box-shadow-datatables shadow-border-2 h-100">
              <div className="card-body">
                <h6 className="fw-semibold mb-3">To Address</h6>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Customer</label>
                      {selectedOrder ? (
                        <input
                          type="text"
                          className="form-control"
                          value={formik.values.customer}
                          readOnly
                          disabled
                          style={{ backgroundColor: "var(--bs-secondary-bg)", cursor: "not-allowed" }}
                        />
                      ) : (
                        <AsyncPaginate
                          classNamePrefix="rs"
                          placeholder="Search customer..."
                          value={selectedCustomer}
                          loadOptions={loadCustomerOptions}
                          onChange={(opt: CustomerOption | null) => {
                            setSelectedCustomer(opt);
                            setSelectedAddressOption(null);
                            formik.setFieldValue("customer", opt?.value ?? "");
                            formik.setFieldValue("customerAddress", "");
                            if (opt) {
                              formik.setFieldValue("toEmail", opt.email ?? "");
                            }
                          }}
                          formatOptionLabel={(option: CustomerOption) => (
                            <div className="d-flex flex-column">
                              <span className="fw-semibold text-dark" style={{ fontSize: "0.95rem" }}>
                                {option.name}
                              </span>
                              <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                                {option.email}
                              </span>
                            </div>
                          )}
                          additional={{ page: 1 }}
                          debounceTimeout={300}
                          defaultOptions
                          isClearable
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: "38px",
                              border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
                              boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
                            }),
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Address</label>
                      {selectedOrder ? (
                        <select
                          className="form-select"
                          value={formik.values.customerAddress}
                          onChange={(e) => {
                            const val = e.target.value;
                            formik.setFieldValue("customerAddress", val);
                            if (val) fillToAddressFromAddressId(val);
                          }}
                        >
                          <option value="">Select Address</option>
                          {customerAddresses.map((addr) => (
                            <option key={addr.value} value={addr.value}>
                              {addr.label}
                            </option>
                          ))}
                        </select>
                      ) : !selectedCustomer ? (
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Select customer first"
                          readOnly
                          disabled
                          style={{ backgroundColor: "var(--bs-secondary-bg)", cursor: "not-allowed" }}
                        />
                      ) : (
                        <AsyncPaginate<AddressSelect2Option, any, { page: number }>
                          classNamePrefix="rs"
                          placeholder=" - Select - "
                          value={selectedAddressOption}
                          loadOptions={loadAddressOptions}
                          onChange={async (opt: AddressSelect2Option | null) => {
                            setSelectedAddressOption(opt);
                            formik.setFieldValue("customerAddress", opt?.value ?? "");
                            if (opt?.value) {
                              await fillToAddressFromAddressId(opt.value);
                            }
                          }}
                          getOptionLabel={(option) => option.label}
                          getOptionValue={(option) => option.value}
                          additional={{ page: 1 }}
                          debounceTimeout={300}
                          defaultOptions
                          isClearable
                          cacheUniqs={[selectedCustomer?.value]}
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: "38px",
                              border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
                              boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
                            }),
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <UIInput formik={formik} name="toFirstName" label="First Name" required />
                  </div>
                  <div className="col-md-6">
                    <UIInput formik={formik} name="toLastName" label="Last Name" required />
                  </div>

                  <div className="col-md-6">
                    <UIInput formik={formik} name="toPhone" label="Phone" required />
                  </div>
                  <div className="col-md-6">
                    <UIInput formik={formik} name="toEmail" label="Email" required />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Street Address <span className="text-danger">*</span>
                  </label>
                  <AddressAutocomplete
                    id="toStreet"
                    name="toStreet"
                    value={formik.values.toStreet}
                    onChange={handleToStreetChange}
                    onSelect={(picked) => {
                      formik.setValues({
                        ...formik.values,
                        toStreet: picked.addressLine1 || "",
                        toCity: picked.city || "",
                        toState: picked.stateCode || picked.state || "",
                        toZip: picked.zipcode || "",
                        toCountry: picked.countryCode || US_COUNTRY_CODE,
                      });
                      setToGoogleAddressLocked(true);
                    }}
                    className="form-control table-cells br-8"
                    placeholder="Enter address"
                    autoComplete="new-password"
                  />
                </div>
                <UIInput formik={formik} name="toAddress2" label="Address 2" />
                <UIInputDisable
                  formik={formik}
                  name="toCity"
                  label="City"
                  required
                  disabled={toGoogleAddressLocked}
                  autocomplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                />

                <div className="mb-3">
                  <label className="form-label">
                    State <span className="text-danger">*</span>
                  </label>
                  {toGoogleAddressLocked ? (
                    <input
                      type="text"
                      className="form-control"
                      name="toState"
                      placeholder="State"
                      value={formik.values.toState}
                      disabled
                      autoComplete="new-password"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  ) : (
                    <select
                      className="form-select"
                      name="toState"
                      value={selectedToStateCode}
                      onChange={(e) => handleToStateChange(e.target.value)}
                      onBlur={formik.handleBlur}
                      autoComplete="new-password"
                    >
                      <option value="">-- Select State --</option>
                      {US_STATES.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <UIInputDisable
                  formik={formik}
                  name="toCountry"
                  label="Country"
                  required
                  disabled
                  autocomplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                />

                <UIInputDisable
                  formik={formik}
                  name="toZip"
                  label="Zip Code"
                  required
                  disabled={toGoogleAddressLocked}
                  autocomplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ================= SHIPPING DETAILS ================= */}
        <div className="p-3">
          <div className="card box-shadow-datatables shadow-border-2 mb-2">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">Shipping Details</h6>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Carrier <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={selectedCarrierId}
                    onChange={(e) => handleCarrierChange(e.target.value)}
                    disabled={loadingCarriers}
                    required
                  >
                    <option value="">
                      {loadingCarriers ? "Loading carriers..." : "Select Carrier"}
                    </option>
                    {carrierOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Package Type <span className="text-danger">*</span>
                  </label>
                  <AsyncPaginate
                    classNamePrefix="rs"
                    placeholder={selectedCarrierName ? "Search package..." : "Select carrier first"}
                    value={selectedPackage}
                    loadOptions={loadPackageOptions as any}
                    // ensure it refreshes when carrier changes
                    cacheUniqs={[selectedCarrierName]}
                    onChange={(opt: any) => {
                      setSelectedPackage(opt);
                      if (!opt) {
                        formik.setFieldValue("packageType", "");
                        return;
                      }

                      // Own packaging => manual dimensions
                      if (opt.value === "own_packaging") {
                        formik.setFieldValue("packageType", "Own packaging");
                        formik.setFieldValue("length", "");
                        formik.setFieldValue("width", "");
                        formik.setFieldValue("height", "");
                        formik.setFieldValue("weight", "");
                        return;
                      }

                      const raw = opt.raw as ShippingPresetSelect2Result | null;
                      formik.setFieldValue("packageType", raw?.text || opt.label);
                      const dim = extractPackageDimensions(raw);
                      if (dim) {
                        formik.setFieldValue("length", dimFieldString(dim.length));
                        formik.setFieldValue("width", dimFieldString(dim.width));
                        formik.setFieldValue("height", dimFieldString(dim.height));
                        // Do NOT auto-load weight from package template (user enters manually)
                        formik.setFieldValue("weight", "");
                      }
                    }}
                    additional={{ page: 1 }}
                    debounceTimeout={300}
                    defaultOptions={!!selectedCarrierName}
                    isClearable
                    isDisabled={!selectedCarrierName}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: "38px",
                        border: state.isFocused ? "1px solid #86b7fe" : "1px solid #ced4da",
                        boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.25)" : "none",
                      }),
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                </div>

                <div className="col-md-3">
                  <UIInput formik={formik} name="length" label="Length (in)" required placeholder="in" />
                </div>
                <div className="col-md-3">
                  <UIInput formik={formik} name="width" label="Width (in)" required placeholder="in" />
                </div>
                <div className="col-md-3">
                  <UIInput formik={formik} name="height" label="Height (in)" required placeholder="in" />
                </div>
                <div className="col-md-3">
                  <UIInput formik={formik} name="weight" label="Weight (lbs)" required placeholder="lbs" />
                </div>
              </div>
            </div>
          </div>
        </div>
              {/* ================= FOOTER ================= */}
      {/* <div className="card box-shadow-datatables shadow-border-2"> */}
        <div className="d-flex justify-content-between px-4 py-4">
          <button className="btn back-btn surface-btn fw-bold fs-16" type="button">
            ← Cancel
          </button>
          <button 
            className="btn btn-primary save-btn surface-btn fw-bold fs-16" 
            type="button"
            onClick={handleGetRates}
            disabled={loadingRates || !selectedCarrierAccountId}
          >
            {loadingRates ? "Loading Rates..." : "Get Discounted Rates"}
          </button>
        </div>
      {/* </div> */}
      </div>

      {/* ================= SHIPPING RATES ================= */}
      <div className="card box-shadow-datatables shadow-border-2 mb-4">
        <div className="px-4">
        {/* Header */}
        <div className="py-4">
          <h3 className="mb-0 text-center">Shipping Rates</h3>
        </div>

        {/* Insurance Section */}
        <div className="">
        <div className="d-flex flex-column align-items-start gap-2">
  <UICheckbox formik={formik} name="addInsurance" label="Add insurance" />

  {formik.values.addInsurance && (
    <div className="insurance-small-input">
      <UIInput
        formik={formik}
        name="insuranceAmount"
        label="Shipment value"
        placeholder="Enter amount"
      />
    </div>
  )}
</div>

        </div>
            <hr className="my-4"/>
        </div>

        {/* Nested Card for Table */}
        <div className="p-0">
          <div className="card box-shadow-datatables shadow-border-2 m-3">
            {/* DESKTOP TABLE (≥ 1022px) */}
            <div className="rdt-desktop">
              <div
                className="production-scroll"
                style={{ "--rdt-min": "800px" } as React.CSSProperties}
              >
                <table className="table production-table align-middle mb-0">
                  <DesktopTableParts table={shippingRatesTable} />
                </table>
              </div>
            </div>

            {/* MOBILE CARDS (≤ 1021px) */}
            <div className="rdt-mobile">
              <MobileCardsOnly
                table={shippingRatesTable}
                idColId="carrierType"
                titleColId="service"
                actionColId="actions"
              />
            </div>

            {/* Empty State */}
            {shippingRates.length === 0 && !loadingRates && (
              <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
                No rates available. Click "Get Discounted Rates" to fetch shipping rates.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= SUCCESS MODAL ================= */}
      {showSuccessModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Label Created Successfully</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseSuccessModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>Success! Your shipping label is ready. Click View Label to preview it, or Download to save the PDF.</p>
                {labelUrl && (
                  <div className="mt-3 d-flex justify-content-end gap-2 flex-wrap">
                    <a
                      href={labelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary rounded-pill px-4"
                    >
                      View Label
                    </a>
                    <button
                      type="button"
                      className="btn btn-outline-primary rounded-pill px-4"
                      onClick={handleDownloadLabel}
                      disabled={downloadingLabel}
                    >
                      {downloadingLabel ? "Downloading..." : "Download"}
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseSuccessModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>
        {`
          .buy-label-btn{
            background-color: #00a63e;
            color:white;
          }
          .buy-label-btn:hover{
            background-color: #008236;
            color:white;
          }
            .insurance-small-input{
  width: 230px;     /* adjust if you want 조금 bigger/smaller */
  max-width: 100%;
}

        `}
      </style>
    </div>
  );
};

export default BuyLabel;
